import { memo, type ReactNode } from 'react';

import { GlobeIcon, PresentationIcon, Settings2Icon, SquareArrowOutUpRightIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { WorkspaceAgentPreviewCardFrame } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardFrame';
import { WorkspaceAgentPreviewCardMetaRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardMetaRow';
import { WorkspaceAgentShowcasePreviewUrlRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentShowcasePreviewUrlRow';

export type WorkspaceAgentShowcasePreviewCardProps = {
  closeButton?: ReactNode;
  /** Public URL with the scheme and a leading `app.` already removed. Omit when no short id exists. */
  displayUrl: string | null;
  onCopyLink: () => void;
  onOpenLink: () => void;
  onOpenSettings: () => void;
  testId?: string;
  thumbnailUrl: string | null;
  title: string;
};

/** Showcase popover body. Copy lives on the URL row when a public link exists. */
export const WorkspaceAgentShowcasePreviewCard = memo(
  ({
    closeButton,
    displayUrl,
    onCopyLink,
    onOpenLink,
    onOpenSettings,
    testId = 'WORKSPACE_AGENT_SHOWCASE_PREVIEW_CARD',
    thumbnailUrl,
    title,
  }: WorkspaceAgentShowcasePreviewCardProps) => {
    const footer = (
      <div className="flex items-center gap-2">
        {displayUrl ? (
          <Button
            appearance="outline"
            className="w-fit"
            color="gray"
            data-testid="WORKSPACE_AGENT_SHOWCASE_PREVIEW_OPEN_LINK"
            onClick={onOpenLink}
            prefix={<SquareArrowOutUpRightIcon aria-hidden size="xs" />}
            size="small"
          >
            Open link
          </Button>
        ) : null}
        <Button
          appearance="outline"
          className="w-fit"
          color="gray"
          data-testid="WORKSPACE_AGENT_SHOWCASE_PREVIEW_SETTINGS"
          onClick={onOpenSettings}
          prefix={<Settings2Icon aria-hidden size="xs" />}
          size="small"
        >
          Settings
        </Button>
      </div>
    );
    const urlRow =
      displayUrl === null ? null : (
        <WorkspaceAgentShowcasePreviewUrlRow displayUrl={displayUrl} onCopyLink={onCopyLink} />
      );

    return (
      <WorkspaceAgentPreviewCardFrame
        closeButton={closeButton}
        footer={footer}
        hasPlayOverlay={false}
        locationLabel={null}
        locationTitle={null}
        mediaFallback={<PresentationIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size={32} />}
        mediaImageUrl={thumbnailUrl}
        testId={testId}
        title={title}
      >
        <WorkspaceAgentPreviewCardMetaRow
          icon={<GlobeIcon aria-hidden size="xs" />}
          testId="WORKSPACE_AGENT_SHOWCASE_PREVIEW_KIND"
        >
          Showcase
        </WorkspaceAgentPreviewCardMetaRow>
        {urlRow}
      </WorkspaceAgentPreviewCardFrame>
    );
  },
);

WorkspaceAgentShowcasePreviewCard.displayName = 'WorkspaceAgentShowcasePreviewCard';
